import type { RespostaConversa } from "@/servidor/tipos";
import type { ListaConversas } from "@/lib/telas/conversas";
import type { Resultado } from "@/lib/telas/casos-conversas";
import type { ResultadoCampanha } from "@/lib/telas/casos-painel";
import { api } from "./api";
import { chaves, queryClient } from "./consultas";

/**
 * O que quem atende faz — cada ação é uma chamada à API, com a resposta que a
 * tela sabe mostrar: `{ ok }` ou `{ erro }`, nunca uma exceção na cara da
 * recepção.
 *
 * Depois de cada uma, o navegador atualiza o que tem guardado — a tela na
 * frente é buscada de novo por trás. E o que a pessoa acabou de fazer aparece
 * NA HORA: a mensagem enviada entra na conversa antes de o servidor responder;
 * se ele recusar, ela sai e a frase do erro aparece.
 */

export type { PreviaCampanha } from "@/lib/telas/casos-painel";

async function chamar(caminho: string, metodo: "POST" | "PUT", corpo: unknown, telefone: string): Promise<Resultado> {
  try {
    return await api<Resultado>(caminho, { method: metodo, body: JSON.stringify(corpo) });
  } catch (e) {
    return { ok: false, erro: e instanceof Error ? e.message : String(e) };
  } finally {
    // Certo ou errado, a verdade é a do servidor: busca de novo por trás.
    void queryClient.invalidateQueries({ queryKey: chaves.conversa(telefone) });
    void queryClient.invalidateQueries({ queryKey: chaves.conversas });
  }
}

/** Mexe na conversa guardada e devolve como desfazer. */
function mexerNaConversa(telefone: string, mudar: (r: RespostaConversa) => RespostaConversa): () => void {
  const chave = chaves.conversa(telefone);
  void queryClient.cancelQueries({ queryKey: chave });
  const antes = queryClient.getQueryData<RespostaConversa>(chave);
  if (antes) queryClient.setQueryData<RespostaConversa>(chave, mudar(antes));
  return () => {
    if (antes) queryClient.setQueryData(chave, antes);
  };
}

const agora = () => new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

export async function responderAction(telefone: string, texto: string): Promise<Resultado> {
  const desfazer = texto.trim()
    ? mexerNaConversa(telefone, (r) => ({
        ...r,
        det: {
          ...r.det,
          mensagens: [
            ...r.det.mensagens,
            { id: `enviando-${Date.now()}`, autor: "humano", texto: texto.trim(), quando: agora(), dia: "Hoje", hora: agora(), emISO: new Date().toISOString() },
          ],
        },
      }))
    : () => {};
  const r = await chamar(`/conversas/${telefone}/responder`, "POST", { texto }, telefone);
  if (!r.ok) desfazer();
  return r;
}

export async function assumirAction(telefone: string, assumir: boolean): Promise<Resultado> {
  const desfazer = mexerNaConversa(telefone, (r) => ({
    ...r,
    det: { ...r.det, atendimentoHumano: assumir },
    quemAssumiu: assumir ? "você" : null,
  }));
  const r = await chamar(`/conversas/${telefone}/assumir`, "POST", { assumir }, telefone);
  if (!r.ok) desfazer();
  return r;
}

export async function enviarModeloAction(telefone: string, nome: string, variaveis: string[]): Promise<Resultado> {
  return chamar(`/conversas/${telefone}/modelo`, "POST", { nome, variaveis }, telefone);
}

export async function definirEtiquetasAction(telefone: string, etiquetas: string[]): Promise<Resultado> {
  const desfazer = mexerNaConversa(telefone, (r) => ({ ...r, det: { ...r.det, etiquetas } }));
  const r = await chamar(`/conversas/${telefone}/etiquetas`, "PUT", { etiquetas }, telefone);
  if (!r.ok) desfazer();
  return r;
}

export async function adicionarNotaAction(telefone: string, texto: string): Promise<Resultado> {
  return chamar(`/conversas/${telefone}/notas`, "POST", { texto }, telefone);
}

/** A faixa some e a conversa sai de "Aguardando a equipe" na hora; o servidor confirma por trás. */
export async function resolverPendenciaAction(telefone: string): Promise<Resultado> {
  const desfazerConversa = mexerNaConversa(telefone, (r) => ({ ...r, det: { ...r.det, pendencia: null } }));
  void queryClient.cancelQueries({ queryKey: chaves.conversas });
  const listaAntes = queryClient.getQueryData<ListaConversas>(chaves.conversas);
  const item = listaAntes?.aguardandoEquipe.find((i) => i.telefone === telefone);
  if (listaAntes && item) {
    const semPendencia = { ...item, pendencia: null };
    queryClient.setQueryData<ListaConversas>(chaves.conversas, {
      ...listaAntes,
      aguardandoEquipe: listaAntes.aguardandoEquipe.filter((i) => i.telefone !== telefone),
      // Na ordem certa ele entra quando a lista voltar do servidor, logo em seguida.
      atendendoVoce: item.atendimentoHumano ? [semPendencia, ...listaAntes.atendendoVoce] : listaAntes.atendendoVoce,
      comOAgente: item.atendimentoHumano ? listaAntes.comOAgente : [semPendencia, ...listaAntes.comOAgente],
    });
  }
  const r = await chamar(`/conversas/${telefone}/pendencia/resolver`, "POST", {}, telefone);
  if (!r.ok) {
    desfazerConversa();
    if (listaAntes) queryClient.setQueryData(chaves.conversas, listaAntes);
  }
  return r;
}

// ─────────────────── fora de uma conversa ───────────────────

/** Chama, e depois marca como velho o que a ação mexeu — a tela na frente se atualiza por trás. */
async function agir<T extends { ok: boolean }>(fazer: () => Promise<T>, mexeuEm: ReadonlyArray<readonly string[]>): Promise<T | Resultado> {
  try {
    return await fazer();
  } catch (e) {
    return { ok: false, erro: e instanceof Error ? e.message : String(e) };
  } finally {
    for (const chave of mexeuEm) void queryClient.invalidateQueries({ queryKey: chave });
  }
}

const corpoJson = (metodo: string, corpo?: unknown): RequestInit => ({ method: metodo, body: corpo === undefined ? undefined : JSON.stringify(corpo) });

export async function marcarPresencaAction(eventoId: string, presenca: "compareceu" | "faltou" | null): Promise<Resultado> {
  return agir(
    () => api<Resultado>(`/agenda/${encodeURIComponent(eventoId)}/presenca`, corpoJson("POST", { presenca })),
    [chaves.agenda, chaves.funil, chaves.relatorio, chaves.pacientes],
  );
}

export async function salvarFichaAction(
  telefone: string,
  dados: { nome?: string | null; interesse?: string | null; observacoes?: string | null },
): Promise<Resultado> {
  return agir(() => api<Resultado>(`/pacientes/${telefone}`, corpoJson("PUT", dados)), [
    chaves.pacientes,
    chaves.funil,
    chaves.conversas,
    chaves.conversa(telefone),
  ]);
}

export async function removerFichaAction(telefone: string): Promise<Resultado> {
  return agir(() => api<Resultado>(`/pacientes/${telefone}`, corpoJson("DELETE")), [chaves.pacientes, chaves.funil, chaves.conversa(telefone)]);
}

export async function salvarInvestimentoAction(mes: string, gasto: number, conversasAnuncio: number | null): Promise<Resultado> {
  return agir(() => api<Resultado>(`/relatorio/gasto/${mes}`, corpoJson("PUT", { gasto, conversasAnuncio })), [chaves.relatorio]);
}

export async function campanhaAction(telefones: string[], nomeModelo: string, manuais: string[], confirmar: boolean): Promise<ResultadoCampanha> {
  // A prévia não muda nada; só o envio de verdade mexe em conversas.
  const mexeu = confirmar ? [chaves.conversas, chaves.pacientes, ["conversa"]] : [];
  return agir(() => api<ResultadoCampanha>("/campanha", corpoJson("POST", { telefones, nomeModelo, manuais, confirmar })), mexeu) as Promise<ResultadoCampanha>;
}
